// Application constants and configuration

// Base path for the app (matches PUBLIC_URL / Router basename)
export const BASE_PATH = process.env.PUBLIC_URL || '';

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost/qms_reactjs_php/apis',
  TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 10000,
  RETRY_ATTEMPTS: 3,
};

// API Endpoints
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    RESET_PASSWORD: '/api/auth/reset-password',
    VERIFY_TOKEN: '/api/auth/verify',
    REFRESH_TOKEN: '/api/auth/refresh',
    VERIFY_OTP: '/api/auth/verify-otp',
    RESEND_OTP: '/api/auth/resend-otp',
    VERIFY_REGISTRATION: '/api/auth/verify-registration',
    CHECK_MOBILE: '/api/auth/check-mobile',
    SEND_DEVICE_OTP: '/api/auth/send-device-otp',
    VERIFY_DEVICE_OTP: '/api/auth/verify-device-otp',
    SET_PASSWORD: '/api/auth/set-password',
    LOGIN_OTP: '/api/auth/login-otp',
    VERIFY_LOGIN_OTP: '/api/auth/verify-otp',
    REGISTER_HOSPITAL: '/api/auth/register-hospital',
    VERIFY_HOSPITAL_REGISTRATION: '/api/auth/verify-hospital-registration',
    CHECK_DOMAIN: '/api/auth/check-domain',
    AADHAAR_SEND_OTP: '/api/auth/aadhaar/send-otp',
    AADHAAR_VERIFY_OTP: '/api/auth/aadhaar/verify-otp',
  },

  // Admin endpoints
  ADMIN: {
    DASHBOARD: '/api/admin/dashboard',

    // Users Management
    USERS: '/api/admin/users',
    USER_BY_ID: (id) => `/api/admin/users/${id}`,
    USERS_EXPORT: '/api/admin/users?export=excel',

    // Roles Management
    ROLES: '/api/admin/roles',
    ROLE_BY_ID: (id) => `/api/admin/roles/${id}`,
    ROLE_PERMISSIONS: (id) => `/api/admin/roles/${id}/permissions`,

    // Support (Staff) Management
    STAFF: '/api/admin/staff',
    STAFF_BY_ID: (id) => `/api/admin/staff/${id}`,
    STAFF_UPDATE: (id) => `/api/admin/staff/update/${id}`,
    STAFF_SEND_MOBILE_OTP: '/api/admin/staff/send-mobile-otp',
    STAFF_VERIFY_MOBILE_OTP: '/api/admin/staff/verify-mobile-otp',

    // Aadhaar Verification
    AADHAAR_SEND_OTP: '/api/admin/aadhaar/send-otp',
    AADHAAR_VERIFY_OTP: '/api/admin/aadhaar/verify-otp',

    // Patient Users Management
    PATIENT_USERS: '/api/admin/patient-users',
    PATIENT_USER_BY_ID: (id) => `/api/admin/patient-users/${id}`,

    // Permissions Management
    PERMISSIONS: '/api/admin/permissions',
    PERMISSION_BY_ID: (id) => `/api/admin/permissions/${id}`,
    PERMISSIONS_BY_ROLE: (roleId) => `/api/admin/permissions/role/${roleId}`,
    PERMISSION_CHECK: (roleId) => `/api/admin/permissions/check/${roleId}`,
    PERMISSIONS_BULK_UPDATE: '/api/admin/permissions/bulk-update',
    PERMISSIONS_TEMPLATES: '/api/admin/permissions/templates',
    PERMISSIONS_MODULES: '/api/admin/permissions/modules',
    PERMISSION_TEMPLATE_APPLY: (roleId) => `/api/admin/permissions/template/${roleId}`,
    PERMISSIONS_COPY: '/api/admin/permissions/copy',
    PERMISSIONS_BULK_CREATE: '/api/admin/permissions/bulk-create',
    PERMISSIONS_BULK_DELETE: '/api/admin/permissions/bulk-delete',
    PERMISSIONS_SYNC: (roleId) => `/api/admin/permissions/sync/${roleId}`,

    // Specializations Management
    SPECIALIZATIONS: '/api/admin/specializations',
    SPECIALIZATION_BY_ID: (id) => `/api/admin/specializations/${id}`,

    // Doctors Management
    DOCTORS: '/api/admin/doctors',
    DOCTOR_BY_ID: (id) => `/api/admin/doctors/${id}`,
    DOCTOR_PHOTO: (id) => `/api/admin/doctors/${id}/photo`,
    DOCTOR_AVAILABILITY: (id) => `/api/admin/doctors/${id}/availability`,
    DOCTOR_SLOTS: (id) => `/api/admin/doctors/${id}/slots`,
    DOCTOR_HOLIDAYS: (id) => `/api/admin/doctors/${id}/holidays`,
    DOCTOR_SORT_ORDER: (id) => `/api/admin/doctors/${id}/sort-order`,

    // Patients Management
    PATIENTS: '/api/admin/patients',
    PATIENT_BY_ID: (id) => `/api/admin/patients/${id}`,
    PATIENT_CREATE_ACCOUNT: (id) => `/api/admin/patients/${id}/create-account`,
    PATIENT_BY_USER_ID: (userId) => `/api/admin/patients/user/${userId}`,
    PATIENT_BY_CODE: (code) => `/api/admin/patients/code/${code}`,
    PATIENTS_SEARCH: '/api/admin/patients/search',

    // Patient Relations
    PATIENT_RELATIONS: '/api/admin/patient-relations',
    PATIENT_RELATIONS_SEARCH: '/api/admin/patient-relations/search-patient',
    PATIENT_RELATIONS_BY_USER: (userId) => `/api/admin/patient-relations/user/${userId}`,
    PATIENT_RELATION_BY_ID: (id) => `/api/admin/patient-relations/${id}`,

    // Services Management
    SERVICES: '/api/admin/services',
    SERVICE_BY_ID: (id) => `/api/admin/services/${id}`,

    // Medicines Management
    MEDICINES: '/api/admin/medicines',
    MEDICINES_SEARCH: '/api/admin/medicines/search',

    // Tests Management
    TESTS: '/api/admin/tests',
    TEST_BY_ID: (id) => `/api/admin/tests/${id}`,
    TESTS_SEARCH: '/api/admin/tests/search',
    TESTS_COST_RANGE: '/api/admin/tests/cost-range',

    // Patient Search
    PATIENT_SEARCH: '/api/admin/patient-search',

    // Appointments Management
    APPOINTMENTS: '/api/admin/appointments',
    APPOINTMENT_BY_ID: (id) => `/api/admin/appointments/${id}`,
    APPOINTMENTS_EXPORT: '/api/admin/appointments/export',
    DEFAULT_NEXT_VISIT_DAYS: '/api/admin/appointments/default-next-visit-days',
    PATIENT_MEDICAL_RECORDS: (patientId) => `/api/admin/appointments/patient/${patientId}/medical-records`,
    UPDATE_BILLING_GST: (billId) => `/api/admin/appointments/billing/${billId}/gst`,

    // Billing Management
    BILLING: '/api/admin/billing',
    BILL_BY_ID: (id) => `/api/admin/billing/${id}`,

    // Payments Management
    PAYMENTS: '/api/admin/payments',

    // Reports Management
    REPORTS_DAILY: '/api/admin/reports/daily',
    REPORTS_DOCTOR: (doctorId) => `/api/admin/reports/doctor/${doctorId}`,
    REPORTS_PATIENT: (patientId) => `/api/admin/reports/patient/${patientId}`,
    REPORTS_APPOINTMENTS: '/api/admin/reports/appointments',
    REPORTS_TESTS: '/api/admin/reports/tests',
    REPORTS_COLLECTIONS: '/api/admin/reports/collections',
    REPORTS_COLLECTION_REPORT: '/api/admin/reports/collection-report',
    REPORTS_CHECKED_PATIENTS: '/api/admin/reports/checked-patients',

    // Doctor Holidays Management (Admin)
    ADMIN_DOCTOR_HOLIDAYS: '/api/admin/doctor-holidays',

    // Availability Slots Management
    AVAILABILITY_SLOTS: '/api/admin/availability-slots',

    // Schedule Override Management
    SCHEDULE_OVERRIDES: '/api/admin/schedule-overrides',

    // Settings
    SETTINGS: '/api/admin/settings',
    SETTINGS_PROFILE: '/api/admin/settings/profile',
    SETTINGS_PASSWORD: '/api/admin/settings/password',
    SETTINGS_PASSWORD_SEND_OTP: '/api/admin/settings/password/send-otp',

    // Business Settings
    BUSINESS_SETTINGS: '/api/admin/business-settings',
    BUSINESS_SETTINGS_FLUSH: '/api/admin/business-settings/flush-data',
    BUSINESS_SETTINGS_FLUSH_SEND_OTP: '/api/admin/business-settings/flush/send-otp',
    BUSINESS_SETTINGS_DOMAIN_SEND_OTP: '/api/admin/business-settings/domain/send-otp',
    BUSINESS_SETTINGS_DOMAIN: '/api/admin/business-settings/domain',
    BUSINESS_SETTINGS_BACKUP: '/api/admin/business-settings/backup',
    TEAM_MEMBERS: '/api/admin/business-settings/team-members',
    TREATMENTS: '/api/admin/treatments',
    DIAGNOSTICS: '/api/admin/diagnostics',
    GALLERY: '/api/admin/gallery',
    TESTIMONIALS: '/api/admin/testimonials',

    // Subscription Management
    SUBSCRIPTION: '/api/admin/subscription',
    SUBSCRIPTION_RENEW: '/api/admin/subscription/renew',
    SUBSCRIPTION_CONFIRM_PAYMENT: '/api/admin/subscription/confirm-payment',
    SUBSCRIPTION_PAYMENTS: '/api/admin/subscription/payments',
    SUBSCRIPTION_CHECK: '/api/admin/subscription/check',
  },

  // Doctor endpoints
  DOCTOR: {
    DASHBOARD: '/api/doctor/dashboard',

    // Users Management
    USERS: '/api/doctor/users',
    USER_BY_ID: (id) => `/api/doctor/users/${id}`,
    USERS_EXPORT: '/api/doctor/users?export=excel',

    // Roles Management
    ROLES: '/api/doctor/roles',
    ROLE_BY_ID: (id) => `/api/doctor/roles/${id}`,
    ROLE_PERMISSIONS: (id) => `/api/doctor/roles/${id}/permissions`,

    // Staff Management
    STAFF: '/api/doctor/staff',
    STAFF_BY_ID: (id) => `/api/doctor/staff/${id}`,
    STAFF_UPDATE: (id) => `/api/doctor/staff/update/${id}`,

    // Patient Users Management
    PATIENT_USERS: '/api/doctor/patient-users',
    PATIENT_USER_BY_ID: (id) => `/api/doctor/patient-users/${id}`,

    // Permissions Management
    PERMISSIONS: '/api/doctor/permissions',
    PERMISSION_BY_ID: (id) => `/api/doctor/permissions/${id}`,
    PERMISSIONS_BY_ROLE: (roleId) => `/api/doctor/permissions/role/${roleId}`,
    PERMISSION_CHECK: (roleId) => `/api/doctor/permissions/check/${roleId}`,
    PERMISSIONS_BULK_UPDATE: '/api/doctor/permissions/bulk-update',
    PERMISSIONS_TEMPLATES: '/api/doctor/permissions/templates',
    PERMISSIONS_MODULES: '/api/doctor/permissions/modules',
    PERMISSION_TEMPLATE_APPLY: (roleId) => `/api/doctor/permissions/template/${roleId}`,
    PERMISSIONS_COPY: '/api/doctor/permissions/copy',
    PERMISSIONS_BULK_CREATE: '/api/doctor/permissions/bulk-create',
    PERMISSIONS_BULK_DELETE: '/api/doctor/permissions/bulk-delete',
    PERMISSIONS_SYNC: (roleId) => `/api/doctor/permissions/sync/${roleId}`,

    // Specializations Management
    SPECIALIZATIONS: '/api/doctor/specializations',
    SPECIALIZATION_BY_ID: (id) => `/api/doctor/specializations/${id}`,

    // Doctors Management
    DOCTORS: '/api/doctor/doctors',
    DOCTOR_BY_ID: (id) => `/api/doctor/doctors/${id}`,
    DOCTOR_PHOTO: (id) => `/api/doctor/doctors/${id}/photo`,
    DOCTOR_AVAILABILITY: (id) => `/api/doctor/doctors/${id}/availability`,
    DOCTOR_SLOTS: (id) => `/api/doctor/doctors/${id}/slots`,
    DOCTOR_HOLIDAYS: (id) => `/api/doctor/doctors/${id}/holidays`,

    // Patients Management
    PATIENTS: '/api/doctor/patients',
    PATIENT_BY_ID: (id) => `/api/doctor/patients/${id}`,
    PATIENT_BY_USER_ID: (userId) => `/api/doctor/patients/user/${userId}`,
    PATIENT_BY_CODE: (code) => `/api/doctor/patients/code/${code}`,
    PATIENTS_SEARCH: '/api/doctor/patients/search',

    // Services Management
    SERVICES: '/api/doctor/services',
    SERVICE_BY_ID: (id) => `/api/doctor/services/${id}`,

    // Tests Management
    TESTS: '/api/doctor/tests',
    TEST_BY_ID: (id) => `/api/doctor/tests/${id}`,
    TESTS_SEARCH: '/api/doctor/tests/search',
    TESTS_COST_RANGE: '/api/doctor/tests/cost-range',

    // Patient Search
    PATIENT_SEARCH: '/api/doctor/patient-search',

    // Appointments Management
    APPOINTMENTS: '/api/doctor/appointments',
    APPOINTMENT_BY_ID: (id) => `/api/doctor/appointments/${id}`,
    APPOINTMENTS_EXPORT: '/api/doctor/appointments/export',
    DEFAULT_NEXT_VISIT_DAYS: '/api/doctor/appointments/default-next-visit-days',
    PATIENT_MEDICAL_RECORDS: (patientId) => `/api/doctor/appointments/patient/${patientId}/medical-records`,
    UPDATE_BILLING_GST: (billId) => `/api/doctor/appointments/billing/${billId}/gst`,

    // Billing Management
    BILLING: '/api/doctor/billing',
    BILL_BY_ID: (id) => `/api/doctor/billing/${id}`,

    // Payments Management
    PAYMENTS: '/api/doctor/payments',

    // Reports Management
    REPORTS_DAILY: '/api/doctor/reports/daily',
    REPORTS_DOCTOR: (doctorId) => `/api/doctor/reports/doctor/${doctorId}`,
    REPORTS_PATIENT: (patientId) => `/api/doctor/reports/patient/${patientId}`,
    REPORTS_APPOINTMENTS: '/api/doctor/reports/appointments',
    REPORTS_TESTS: '/api/doctor/reports/tests',
    REPORTS_COLLECTIONS: '/api/doctor/reports/collections',
    REPORTS_COLLECTION_REPORT: '/api/doctor/reports/collection-report',
    REPORTS_CHECKED_PATIENTS: '/api/doctor/reports/checked-patients',

    // Doctor Holidays Management (Admin)
    ADMIN_DOCTOR_HOLIDAYS: '/api/doctor/doctor-holidays',

    // Availability Slots Management
    AVAILABILITY_SLOTS: '/api/doctor/availability-slots',

    // Schedule Override Management
    SCHEDULE_OVERRIDES: '/api/doctor/schedule-overrides',

    // Medicines
    MEDICINES: '/api/doctor/medicines',
    MEDICINES_SEARCH: '/api/doctor/medicines/search',
    MEDICINE_BY_ID: (id) => `/api/doctor/medicines/${id}`,

    // Settings
    SETTINGS: '/api/doctor/settings',
    SETTINGS_PROFILE: '/api/doctor/settings/profile',
    SETTINGS_PASSWORD: '/api/doctor/settings/password',
    SETTINGS_PASSWORD_SEND_OTP: '/api/doctor/settings/password/send-otp',
    SETTINGS_PHOTO: '/api/doctor/settings/photo',
  },

  // Staff endpoints
  STAFF: {
    DASHBOARD: '/api/staff/dashboard',

    // Users Management
    USERS: '/api/staff/users',
    USER_BY_ID: (id) => `/api/staff/users/${id}`,
    USERS_EXPORT: '/api/staff/users?export=excel',

    // Roles Management
    ROLES: '/api/staff/roles',
    ROLE_BY_ID: (id) => `/api/staff/roles/${id}`,
    ROLE_PERMISSIONS: (id) => `/api/staff/roles/${id}/permissions`,

    // Staff Management
    STAFF: '/api/staff/staff',
    STAFF_BY_ID: (id) => `/api/staff/staff/${id}`,
    STAFF_UPDATE: (id) => `/api/staff/staff/update/${id}`,

    // Patient Users Management
    PATIENT_USERS: '/api/staff/patient-users',
    PATIENT_USER_BY_ID: (id) => `/api/staff/patient-users/${id}`,

    // Permissions Management
    PERMISSIONS: '/api/staff/permissions',
    PERMISSION_BY_ID: (id) => `/api/staff/permissions/${id}`,
    PERMISSIONS_BY_ROLE: (roleId) => `/api/staff/permissions/role/${roleId}`,
    PERMISSION_CHECK: (roleId) => `/api/staff/permissions/check/${roleId}`,
    PERMISSIONS_BULK_UPDATE: '/api/staff/permissions/bulk-update',
    PERMISSIONS_TEMPLATES: '/api/staff/permissions/templates',
    PERMISSIONS_MODULES: '/api/staff/permissions/modules',
    PERMISSION_TEMPLATE_APPLY: (roleId) => `/api/staff/permissions/template/${roleId}`,
    PERMISSIONS_COPY: '/api/staff/permissions/copy',
    PERMISSIONS_BULK_CREATE: '/api/staff/permissions/bulk-create',
    PERMISSIONS_BULK_DELETE: '/api/staff/permissions/bulk-delete',
    PERMISSIONS_SYNC: (roleId) => `/api/staff/permissions/sync/${roleId}`,

    // Specializations Management
    SPECIALIZATIONS: '/api/staff/specializations',
    SPECIALIZATION_BY_ID: (id) => `/api/staff/specializations/${id}`,

    // Doctors Management
    DOCTORS: '/api/staff/doctors',
    DOCTOR_BY_ID: (id) => `/api/staff/doctors/${id}`,
    DOCTOR_PHOTO: (id) => `/api/staff/doctors/${id}/photo`,
    DOCTOR_AVAILABILITY: (id) => `/api/staff/doctors/${id}/availability`,
    DOCTOR_SLOTS: (id) => `/api/staff/doctors/${id}/slots`,
    DOCTOR_HOLIDAYS: (id) => `/api/staff/doctors/${id}/holidays`,

    // Patients Management
    PATIENTS: '/api/staff/patients',
    PATIENT_BY_ID: (id) => `/api/staff/patients/${id}`,
    PATIENT_BY_USER_ID: (userId) => `/api/staff/patients/user/${userId}`,
    PATIENT_BY_CODE: (code) => `/api/staff/patients/code/${code}`,
    PATIENTS_SEARCH: '/api/staff/patients/search',
    PATIENT_CREATE_ACCOUNT: (id) => `/api/staff/patients/${id}/create-account`,

    // Patient Relations
    PATIENT_RELATIONS: '/api/staff/patient-relations',
    PATIENT_RELATIONS_SEARCH: '/api/staff/patient-relations/search-patient',
    PATIENT_RELATIONS_BY_USER: (userId) => `/api/staff/patient-relations/user/${userId}`,

    // Services Management
    SERVICES: '/api/staff/services',
    SERVICE_BY_ID: (id) => `/api/staff/services/${id}`,

    // Medicines Management
    MEDICINES: '/api/staff/medicines',
    MEDICINES_SEARCH: '/api/staff/medicines/search',

    // Tests Management
    TESTS: '/api/staff/tests',
    TEST_BY_ID: (id) => `/api/staff/tests/${id}`,
    TESTS_SEARCH: '/api/staff/tests/search',
    TESTS_COST_RANGE: '/api/staff/tests/cost-range',

    // Patient Search
    PATIENT_SEARCH: '/api/staff/patient-search',

    // Appointments Management
    APPOINTMENTS: '/api/staff/appointments',
    APPOINTMENT_BY_ID: (id) => `/api/staff/appointments/${id}`,
    APPOINTMENTS_EXPORT: '/api/staff/appointments/export',
    DEFAULT_NEXT_VISIT_DAYS: '/api/staff/appointments/default-next-visit-days',
    PATIENT_MEDICAL_RECORDS: (patientId) => `/api/staff/appointments/patient/${patientId}/medical-records`,
    UPDATE_BILLING_GST: (billId) => `/api/staff/appointments/billing/${billId}/gst`,

    // Billing Management
    BILLING: '/api/staff/billing',
    BILL_BY_ID: (id) => `/api/staff/billing/${id}`,

    // Payments Management
    PAYMENTS: '/api/staff/payments',

    // Reports Management
    REPORTS_DAILY: '/api/staff/reports/daily',
    REPORTS_DOCTOR: (doctorId) => `/api/staff/reports/doctor/${doctorId}`,
    REPORTS_PATIENT: (patientId) => `/api/staff/reports/patient/${patientId}`,
    REPORTS_APPOINTMENTS: '/api/staff/reports/appointments',
    REPORTS_TESTS: '/api/staff/reports/tests',
    REPORTS_COLLECTIONS: '/api/staff/reports/collections',
    REPORTS_COLLECTION_REPORT: '/api/staff/reports/collection-report',
    REPORTS_CHECKED_PATIENTS: '/api/staff/reports/checked-patients',

    // Doctor Holidays Management (Admin)
    ADMIN_DOCTOR_HOLIDAYS: '/api/staff/doctor-holidays',

    // Availability Slots Management
    AVAILABILITY_SLOTS: '/api/staff/availability-slots',

    // Settings
    SETTINGS: '/api/staff/settings',
    SETTINGS_PROFILE: '/api/staff/settings/profile',
    SETTINGS_PASSWORD: '/api/staff/settings/password',
    SETTINGS_PASSWORD_SEND_OTP: '/api/staff/settings/password/send-otp',

    // Schedule Overrides
    SCHEDULE_OVERRIDES: '/api/staff/schedule-overrides',

    // Aadhaar Verification
    AADHAAR_SEND_OTP: '/api/staff/aadhaar/send-otp',
    AADHAAR_VERIFY_OTP: '/api/staff/aadhaar/verify-otp',

    // Business Settings
    BUSINESS_SETTINGS: '/api/staff/business-settings',
  },

  // Super Admin endpoints
  SUPER_ADMIN: {
    DASHBOARD: '/api/superadmin/dashboard',

    // User Approvals
    USER_APPROVALS: '/api/superadmin/user-approvals',
    USER_APPROVAL_BY_ID: (id) => `/api/superadmin/user-approvals/${id}`,

    // Subscription Plans
    SUBSCRIPTION_PLANS: '/api/superadmin/subscription-plans',
    SUBSCRIPTION_PLAN_BY_ID: (id) => `/api/superadmin/subscription-plans/${id}`,

    // User Subscriptions
    SUBSCRIPTIONS: '/api/superadmin/subscriptions',
    SUBSCRIPTION_BY_ID: (id) => `/api/superadmin/subscriptions/${id}`,
    SUBSCRIPTION_GRANT_GRACE: (id) => `/api/superadmin/subscriptions/${id}/grant-grace`,

    // Coupons
    COUPONS: '/api/superadmin/coupons',
    COUPON_BY_ID: (id) => `/api/superadmin/coupons/${id}`,
    COUPON_USAGE: (id) => `/api/superadmin/coupons/${id}/usage`,
    COUPON_VALIDATE: '/api/superadmin/coupons/validate',
    COUPON_ADMIN_USERS: '/api/superadmin/coupons/admin-users',

    // Settings
    SETTINGS: '/api/superadmin/settings',
    SETTINGS_PROFILE: '/api/superadmin/settings/profile',
    SETTINGS_PASSWORD: '/api/superadmin/settings/password',
    SETTINGS_PASSWORD_SEND_OTP: '/api/superadmin/settings/password/send-otp',
    SETTINGS_WEBHOOK: '/api/superadmin/settings/webhook',
    SETTINGS_WEBHOOK_TEST: '/api/superadmin/settings/webhook/test',

    // Impersonation
    IMPERSONATE_ADMIN: (id) => `/api/superadmin/user-approvals/${id}/impersonate`,

    // User Directory
    USER_DIRECTORY: '/api/superadmin/users',
    USER_TREE: '/api/superadmin/users/tree',
    USER_TREE_CHILDREN: (adminId) => `/api/superadmin/users/tree/${adminId}`,
    IMPERSONATE_USER: (id) => `/api/superadmin/users/${id}/impersonate`,
  },

  // Patient (User) endpoints
  PATIENT: {
    PROFILE: '/api/user/profile',
    APPOINTMENTS: '/api/user/appointments',
    APPOINTMENT_BY_ID: (id) => `/api/user/appointments/${id}`,
    DOCTORS: '/api/doctors',
    DOCTOR_BY_ID: (id) => `/api/doctors/${id}`,
    DOCTOR_SLOTS: '/api/doctor_slots',
    SPECIALIZATIONS: '/api/specializations',
    PRESCRIPTIONS: '/api/user/prescriptions',
    PRESCRIPTION_BY_ID: (id) => `/api/user/prescriptions/${id}`,
    BILLS: '/api/user/bills',
    BILL_BY_ID: (id) => `/api/user/bills/${id}`,

    // Settings & Account
    SETTINGS: '/api/user/settings',
    SETTINGS_PROFILE: '/api/user/settings/profile',
    SETTINGS_PASSWORD: '/api/user/settings/password',
    SETTINGS_PASSWORD_SEND_OTP: '/api/user/settings/password/send-otp',
    SETTINGS_PROFILE_PHOTO: '/api/user/settings/profile/photo',
    MEDICAL_RECORDS: '/api/user/settings/medical-records',
    FAMILY_MEMBERS: '/api/user/settings/family',
    FAMILY_MEMBER_BY_ID: (id) => `/api/user/settings/family/${id}`,
    UPDATE_BILLING_GST: (billId) => `/api/user/appointments/billing/${billId}/gst`,
  },
};

// User Roles
export const USER_ROLES = {
  SUPER_ADMIN: 'superadmin',
  ADMIN: 'admin',
  DOCTOR: 'doctor',
  STAFF: 'staff',
  PATIENT: 'patient',
};

// Role-based routes
export const ROLE_ROUTES = {
  [USER_ROLES.SUPER_ADMIN]: '/superadmin/dashboard',
  [USER_ROLES.ADMIN]: '/admin/home',
  [USER_ROLES.DOCTOR]: '/doctor/home',
  [USER_ROLES.STAFF]: '/staff/home',
  [USER_ROLES.PATIENT]: '/patient/home',
};

// Local Storage Keys
export const STORAGE_KEYS = {
  USER: 'hospitalUser',
  TOKEN: 'authToken',
  REMEMBER_CREDENTIALS: 'rememberedCredentials',
  SUPERADMIN_SESSION: 'superadminSession',
  SUBSCRIPTION_REMINDER_LAST: 'subscriptionReminderLast',
};

// Get tenant domain from current browser context
export const getTenantDomain = () => {
  // 1. Check ?domain= query param (dev override)
  const urlParams = new URLSearchParams(window.location.search);
  const domainParam = urlParams.get('domain');
  if (domainParam) return domainParam.toLowerCase();

  // 2. Use hostname — skip localhost, loopback, and private/LAN IPs
  const hostname = window.location.hostname.toLowerCase();
  if (
    ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname) ||
    /^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.)/.test(hostname)
  )
    return null;
  return hostname;
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'DD/MM/YYYY',
  API: 'YYYY-MM-DD',
  DATETIME: 'DD/MM/YYYY HH:mm',
  TIME: 'HH:mm',
};

// Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied.',
  NOT_FOUND: 'Resource not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  INVALID_CREDENTIALS: 'Invalid credentials. Please check your mobile number and password.',
  ACCOUNT_DEACTIVATED: 'Account is deactivated. Please contact administrator.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful',
  LOGOUT_SUCCESS: 'Logout successful',
  SAVE_SUCCESS: 'Data saved successfully',
  UPDATE_SUCCESS: 'Data updated successfully',
  DELETE_SUCCESS: 'Data deleted successfully',
};
